import { useMemo, useState } from 'react';
import { invokeEdgeWithAuthRetry } from '@/lib/invoke-edge-auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Bot, Loader2, MessageSquare, Send, X } from 'lucide-react';

type Citation = {
  sourceId: string;
  title: string;
};

type AssistantMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
};

type Props = {
  enabled: boolean;
  rolloutKnown: boolean;
  contextDomainId?: string | null;
  contextAccountId?: string | null;
};

const MAX_MESSAGES = 40;

export function AdminAiAssistantWidget({
  enabled,
  rolloutKnown,
  contextDomainId,
  contextAccountId,
}: Props) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);

  const contextSummary = useMemo(() => {
    const parts: string[] = [];
    if (contextDomainId) parts.push('Domain context active');
    if (contextAccountId) parts.push('Account context active');
    return parts.join(' · ');
  }, [contextAccountId, contextDomainId]);

  const appendMessage = (message: AssistantMessage) => {
    setMessages((prev) => [...prev, message].slice(-MAX_MESSAGES));
  };

  const handleSend = async () => {
    const message = input.trim();
    if (!message || sending) return;

    appendMessage({
      id: `u-${Date.now()}`,
      role: 'user',
      content: message,
    });
    setInput('');
    setSending(true);

    try {
      const { data, error } = await invokeEdgeWithAuthRetry<any>('admin-ai-assistant', {
        body: {
          message,
          domainId: contextDomainId || undefined,
          accountId: contextAccountId || undefined,
          threadId: threadId || undefined,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (typeof data?.threadId === 'string' && data.threadId) {
        setThreadId(data.threadId);
      }

      appendMessage({
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: typeof data?.answer === 'string' && data.answer.trim().length > 0
          ? data.answer
          : 'No answer was returned.',
        citations: Array.isArray(data?.citations)
          ? data.citations
              .map((citation: any) => ({
                sourceId: typeof citation?.sourceId === 'string' ? citation.sourceId : '',
                title: typeof citation?.title === 'string' ? citation.title : '',
              }))
              .filter((citation: Citation) => citation.sourceId)
          : [],
      });
    } catch (err: any) {
      console.error('admin-ai-assistant invoke failed:', err);
      appendMessage({
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: 'I could not complete that request right now.',
      });
      toast.error(err?.message || 'AI assistant unavailable');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[70]">
      {!open ? (
        <Button
          type="button"
          onClick={() => setOpen(true)}
          className="h-12 rounded-full px-4 shadow-xl"
          variant="default"
        >
          <Bot className="h-4 w-4 mr-2" />
          Admin AI
        </Button>
      ) : (
        <div className="w-[min(92vw,380px)] rounded-xl border border-border/60 bg-background shadow-2xl">
          <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <div className="text-sm font-medium">Admin AI Assistant</div>
            </div>
            <Button type="button" size="icon" variant="ghost" onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="px-3 pt-2 text-xs text-muted-foreground">
            Read-only answers from current admin portal data.
          </div>
          {contextSummary ? (
            <div className="px-3 pt-1 text-[11px] text-muted-foreground">{contextSummary}</div>
          ) : null}

          {!rolloutKnown ? (
            <div className="px-3 py-4 text-sm text-muted-foreground">Loading assistant settings...</div>
          ) : !enabled ? (
            <div className="px-3 py-4 text-sm text-muted-foreground">
              AI assistant is currently disabled by rollout settings.
            </div>
          ) : (
            <>
              <div className="max-h-[340px] overflow-y-auto px-3 py-3 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Ask about alerts, posture, diagnostics, or policy states.
                  </div>
                ) : (
                  messages.map((message) => (
                    <div
                      key={message.id}
                      className={`rounded-md border px-3 py-2 text-sm ${
                        message.role === 'user'
                          ? 'border-primary/40 bg-primary/10'
                          : 'border-border/60 bg-muted/20'
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{message.content}</div>
                      {message.role === 'assistant' && message.citations && message.citations.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {message.citations.map((citation) => (
                            <Badge key={`${message.id}-${citation.sourceId}`} variant="outline" className="text-[10px]">
                              {citation.sourceId}: {citation.title}
                            </Badge>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
                {sending ? (
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Thinking...
                  </div>
                ) : null}
              </div>

              <div className="border-t border-border/60 p-3 space-y-2">
                <Input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="Ask about alerts or config..."
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      void handleSend();
                    }
                  }}
                  disabled={sending}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void handleSend()}
                    disabled={sending || input.trim().length === 0}
                  >
                    {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    <span className="ml-1">Send</span>
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
